import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type PropsWithChildren,
} from "react";
import {
  useWebSocketClient,
  type UseWebSocketClientParams,
} from "./websocket.client";
import type { WebSocketOutboundMessage } from "./websocket.utils";

export type WebSocketProviderProps =
  PropsWithChildren<UseWebSocketClientParams>;

type WebSocketSubscriber = {
  onOpen?: (event: Event) => void;
  onMessage?: (event: MessageEvent) => void;
  onError?: (event: Event) => void;
  onClose?: (event: CloseEvent) => void;
};

type WebSocketContextValue = Omit<
  ReturnType<typeof useWebSocketClient>,
  "sendJsonMessage" | "sendMessage"
> & {
  subscribe: (subscriber: WebSocketSubscriber) => () => void;
  sendMessage: (message: WebSocketOutboundMessage) => void;
};

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

export function WebSocketProvider({
  children,
  ...props
}: WebSocketProviderProps) {
  const subscribersRef = useRef(new Set<WebSocketSubscriber>());

  const subscribe = useCallback((subscriber: WebSocketSubscriber) => {
    subscribersRef.current.add(subscriber);
    return () => {
      subscribersRef.current.delete(subscriber);
    };
  }, []);

  const websocket = useWebSocketClient({
    ...props,
    onOpen: (event) => {
      props.onOpen?.(event);
      for (const subscriber of subscribersRef.current) {
        subscriber.onOpen?.(event);
      }
    },
    onMessage: (event) => {
      props.onMessage?.(event);
      for (const subscriber of subscribersRef.current) {
        subscriber.onMessage?.(event);
      }
    },
    onError: (event) => {
      props.onError?.(event);
      for (const subscriber of subscribersRef.current) {
        subscriber.onError?.(event);
      }
    },
    onClose: (event) => {
      props.onClose?.(event);
      for (const subscriber of subscribersRef.current) {
        subscriber.onClose?.(event);
      }
    },
  });

  const value = useMemo(
    () =>
      Object.assign({}, websocket, {
        subscribe,
        sendMessage: (message: WebSocketOutboundMessage) => {
          websocket.sendJsonMessage(message);
        },
      }),
    [websocket, subscribe],
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);

  if (context == null) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }

  return context;
}
